// Reusable resumable-upload hook backing both raw-profiles
// (`/api/upload/tus`) and the API tarball channel (`/api/upload/tus-api`).
//
// Explicit four-state machine (`idle | uploading | error | done`) backed
// by a single `tus.Upload` instance held in a ref. Progress is derived
// from a 5-second sliding window of `(timestamp, loaded)` samples taken
// every 500 ms, so the displayed rate doesn't jitter on every PATCH
// callback the way an instantaneous loaded/elapsed would.
//
// The shape of the "completed bundle" is left to the caller via the
// `resolveCompleted(headerValue)` callback so each channel can derive
// whatever metadata it cares about (UUID for profiles, filename + sidecar
// inspection for API tarballs) once the final PATCH lands.

import { useCallback, useEffect, useRef, useState } from "react";
import * as tus from "tus-js-client";

export type UploadState = "idle" | "uploading" | "error" | "done";

interface RateSample {
	t: number;
	loaded: number;
}

export interface UploadStats {
	loaded: number;
	total: number;
	rate: number; // bytes/sec
	eta: number; // seconds, Infinity if unknown
}

export const ZERO_UPLOAD_STATS: UploadStats = {
	loaded: 0,
	total: 0,
	rate: 0,
	eta: Infinity,
};

export interface UseTusUploadResult<TBundle> {
	state: UploadState;
	file: File | null;
	stats: UploadStats;
	error: string | null;
	bundle: TBundle | null;
	start: (file: File) => void;
	retry: () => void;
	cancel: () => void;
	reset: () => void;
}

interface UseTusUploadOptions<TBundle> {
	endpoint: string;
	/**
	 * Header the server returns on the final PATCH so the client can
	 * resolve the bundle row. Defaults to `X-Bundle-Id` (raw-profiles
	 * channel); the API tarball channel uses `X-API-Bundle-Filename`.
	 */
	successHeader?: string;
	/**
	 * Resolves the final bundle metadata after the upload completes. The
	 * server is expected to set `successHeader`; the resolver may use that
	 * to fetch a richer row from a list endpoint.
	 */
	resolveCompleted: (headerValue: string | null) => Promise<TBundle | null>;
	onComplete?: (bundle: TBundle) => void;
	chunkSize?: number;
	/**
	 * Whether tus-js-client should persist a fingerprint to localStorage
	 * for resuming this upload across page reloads. Defaults to `true`
	 * for parity with the historical profiles flow. The API channel
	 * passes `false` because (a) API tarballs are 5–50 MiB, so resume is
	 * not worth much, and (b) a stale fingerprint pointing at an upload
	 * created under a previous session has been observed to surface as
	 * an opaque 401 mid-PATCH.
	 */
	resumable?: boolean;
}

const DEFAULT_CHUNK_SIZE = 16 * 1024 * 1024;

export function useTusUpload<TBundle>(
	opts: UseTusUploadOptions<TBundle>,
): UseTusUploadResult<TBundle> {
	const {
		endpoint,
		successHeader = "X-Bundle-Id",
		resolveCompleted,
		onComplete,
		chunkSize = DEFAULT_CHUNK_SIZE,
		resumable = true,
	} = opts;

	const [state, setState] = useState<UploadState>("idle");
	const [file, setFile] = useState<File | null>(null);
	const [stats, setStats] = useState<UploadStats>(ZERO_UPLOAD_STATS);
	const [error, setError] = useState<string | null>(null);
	const [bundle, setBundle] = useState<TBundle | null>(null);

	const uploadRef = useRef<tus.Upload | null>(null);
	const samplesRef = useRef<RateSample[]>([]);
	const sampleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const lastHeaderRef = useRef<string | null>(null);

	const stopSampler = useCallback(() => {
		if (sampleTimerRef.current) {
			clearInterval(sampleTimerRef.current);
			sampleTimerRef.current = null;
		}
		samplesRef.current = [];
	}, []);

	const computeRate = useCallback(
		(loaded: number, total: number): UploadStats => {
			const now = performance.now();
			samplesRef.current.push({ t: now, loaded });
			const cutoff = now - 5000;
			while (
				samplesRef.current.length > 1 &&
				(samplesRef.current[0]?.t ?? now) < cutoff
			) {
				samplesRef.current.shift();
			}
			const samples = samplesRef.current;
			const first = samples[0] ?? { t: now, loaded };
			const last = samples[samples.length - 1] ?? first;
			const dt = (last.t - first.t) / 1000;
			const dBytes = last.loaded - first.loaded;
			const rate = dt > 0 ? dBytes / dt : 0;
			const remaining = total - loaded;
			const eta = rate > 0 ? remaining / rate : Infinity;
			return { loaded, total, rate, eta };
		},
		[],
	);

	const startUpload = useCallback(
		(target: File) => {
			lastHeaderRef.current = null;
			setError(null);
			setBundle(null);
			setStats({ loaded: 0, total: target.size, rate: 0, eta: Infinity });
			setState("uploading");
			samplesRef.current = [{ t: performance.now(), loaded: 0 }];

			const upload = new tus.Upload(target, {
				endpoint,
				chunkSize,
				retryDelays: [0, 1000, 3000, 5000, 10000],
				removeFingerprintOnSuccess: true,
				storeFingerprintForResuming: resumable,
				metadata: {
					filename: target.name,
					filetype: target.type || "application/octet-stream",
				},
				onAfterResponse: (_req, res) => {
					const v = res.getHeader(successHeader);
					if (v) lastHeaderRef.current = v;
				},
				onProgress: (loaded, total) => {
					setStats(computeRate(loaded, total));
				},
				onError: (err) => {
					stopSampler();
					setError(humanizeUploadError(err));
					setState("error");
				},
				onSuccess: async () => {
					stopSampler();
					try {
						const resolved = await resolveCompleted(lastHeaderRef.current);
						if (!resolved) {
							throw new Error(
								"Upload completed but the server did not return a usable bundle reference",
							);
						}
						setBundle(resolved);
						setState("done");
						onComplete?.(resolved);
					} catch (err) {
						setError(err instanceof Error ? err.message : String(err));
						setState("error");
					}
				},
			});

			uploadRef.current = upload;
			setFile(target);

			sampleTimerRef.current = setInterval(() => {
				setStats((prev) => {
					if (prev.total <= 0) return prev;
					return computeRate(prev.loaded, prev.total);
				});
			}, 500);

			upload.start();
		},
		[
			endpoint,
			chunkSize,
			successHeader,
			resolveCompleted,
			computeRate,
			stopSampler,
			onComplete,
			resumable,
		],
	);

	const start = useCallback(
		(target: File) => {
			if (uploadRef.current) {
				uploadRef.current.abort(true).catch(() => {});
			}
			startUpload(target);
		},
		[startUpload],
	);

	const retry = useCallback(() => {
		if (!file) return;
		startUpload(file);
	}, [file, startUpload]);

	const cancel = useCallback(() => {
		const u = uploadRef.current;
		uploadRef.current = null;
		stopSampler();
		setState("idle");
		setFile(null);
		setStats(ZERO_UPLOAD_STATS);
		setError(null);
		setBundle(null);
		if (u) {
			u.abort(true).catch(() => {});
		}
	}, [stopSampler]);

	const reset = useCallback(() => {
		uploadRef.current = null;
		stopSampler();
		setState("idle");
		setFile(null);
		setStats(ZERO_UPLOAD_STATS);
		setError(null);
		setBundle(null);
	}, [stopSampler]);

	useEffect(() => {
		return () => {
			stopSampler();
			if (uploadRef.current) {
				uploadRef.current.abort(true).catch(() => {});
				uploadRef.current = null;
			}
		};
	}, [stopSampler]);

	return { state, file, stats, error, bundle, start, retry, cancel, reset };
}

/**
 * Translate a tus-js-client error into a user-facing string. The library
 * surfaces network and protocol failures as `Error` instances that
 * stringify into "tus: unexpected response while uploading chunk,
 * originated from request (method: PATCH, url: …, response code: 401,
 * response text: …)" — useful for debugging, terrible to put in front
 * of an operator. We special-case 401 (the most common cause of a
 * mid-upload failure: the dev server restarted between POST and PATCH
 * and dropped the session) and otherwise fall through to the raw
 * message.
 */
function humanizeUploadError(err: unknown): string {
	const raw = err instanceof Error ? err.message : String(err);
	const orig = (err as { originalResponse?: { getStatus?: () => number } })
		?.originalResponse;
	const status =
		typeof orig?.getStatus === "function" ? orig.getStatus() : null;
	if (status === 401 || /response code:\s*401/i.test(raw)) {
		return "Session expired — refresh the page and log in again, then retry the upload.";
	}
	if (status === 403 || /response code:\s*403/i.test(raw)) {
		return "Operator role required to upload bundles.";
	}
	return raw;
}

export function fmtRate(bytesPerSec: number, bytesFmt: (n: number) => string) {
	if (!Number.isFinite(bytesPerSec) || bytesPerSec <= 0) return "—";
	return `${bytesFmt(bytesPerSec)}/s`;
}

export function fmtEta(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "—";
	if (seconds < 60) return `${Math.ceil(seconds)}s`;
	const m = Math.floor(seconds / 60);
	const s = Math.ceil(seconds % 60);
	if (m < 60) return `${m}m ${s.toString().padStart(2, "0")}s`;
	const h = Math.floor(m / 60);
	return `${h}h ${(m % 60).toString().padStart(2, "0")}m`;
}
